<template>
  <v-container
    v-if="!products?.length && Object.keys(productsStore.filterProductCondition).length === 0"
  >
    <div v-for="product in products" :key="product.id">
      {{ product.name }}
    </div>
  </v-container>

  <v-row>
    <v-col sm="12" md="12">
      <v-card v-if="productsStore.noResults" align="center" class="pa-3">
        {{ $t("common.action.noResultsFound") }}</v-card
      >
      <v-card v-else>
        <v-data-table
          id="productTable"
          :headers="headers"
          :items="products"
          hide-default-footer
          :loading="productsListLoading"
          v-model:sort-by="sortBy"
          loading-text="Loading products..."
          @update:sort-by="onSortByUpdate"
        >
          <template v-slot:item="{ item }">
            <tr
              @click="viewProductDetails((item as ProductTableRow).id)"
              :key="(item as ProductTableRow).id"
              style="cursor: pointer"
            >
              <!-- Image -->
              <td class="text-start">
                <v-avatar
                  :image="
                    productsStore.getProductDefaultImage(
                      (item as ProductTableRow).images,
                      getProductClass(item as ProductTableRow) ?? undefined,
                    )
                  "
                />
              </td>

              <td class="text-start">
                <div>{{ (item as ProductTableRow).name }}</div>

                <div v-if="(item as ProductTableRow).description" class="text-caption">
                  <span
                    v-truncate="[(item as ProductTableRow).description, 30]"
                    class="text-grey"
                  />
                </div>
              </td>

              <!-- Product Class column - only show if not on product class details page -->
              <td v-if="!isProductClassDetailsPage" class="text-start">
                <div v-if="getProductClass(item as ProductTableRow)">
                  <div>{{ getProductClass(item as ProductTableRow)?.name }}</div>
                  <div
                    v-if="getProductClass(item as ProductTableRow)?.description"
                    class="text-caption"
                  >
                    <span
                      v-truncate="[getProductClass(item as ProductTableRow)?.description, 30]"
                      class="text-grey"
                    />
                  </div>
                </div>
                <span v-else>-</span>
              </td>
              <td class="text-center">
                <status-chip :status="(item as ProductTableRow).active" />
              </td>
              <td class="text-center" v-if="!isProductTagDetailsPage">
                <div
                  v-if="hasProductTags(item as ProductTableRow)"
                  class="d-flex flex-wrap gap-1 justify-center"
                >
                  <span v-for="tag in getProductTags(item as ProductTableRow)" :key="tag.id">
                    <product-tag-chip
                      :color="String(tag.hexColor)"
                      :label="tag.name"
                      class="ma-1"
                    />
                  </span>
                </div>
                <span v-else class="text-black text-subtitle-1">-</span>
              </td>
              <td class="text-end" v-if="hasFeatureAccess(PlatformFeature.INVENTORY)">
                <template v-if="hasProductInventory(item as ProductTableRow)">
                  <product-stock-chips
                    v-if="showInventoryChips"
                    :product="item as ProductTableRow"
                    :location-id="stockChipsLocationId"
                    :stock-count="Number((item as ProductTableRow).stockCount ?? 0)"
                    :low-stock-threshold="(item as ProductTableRow).lowStockThreshold ?? undefined"
                    :checkout-count="Number((item as ProductTableRow).checkoutCount ?? 0)"
                  />
                  <v-tooltip v-else location="top">
                    <template v-slot:activator="{ props }">
                      <span v-bind="props" class="text-grey text-caption">
                        <v-icon size="small" color="grey-lighten-1"
                          >mdi-map-marker-multiple-outline</v-icon
                        >
                        {{ $t("inventory.locationBased") }}
                      </span>
                    </template>
                    <span>{{ $t("inventory.locationBasedInventory") }}</span>
                  </v-tooltip>
                </template>
                <v-tooltip v-else location="top">
                  <template v-slot:activator="{ props }">
                    <span v-bind="props" class="text-grey text-caption">
                      <v-icon size="small" color="grey-lighten-1">mdi-minus-circle-outline</v-icon>
                      {{ $t("inventory.notTracked") }}
                    </span>
                  </template>
                  <span>{{ $t("inventory.inventoryNotTracked") }}</span>
                </v-tooltip>
              </td>
              <td v-if="showGlobalLocationBasedUI" class="text-center">
                {{ getProductLocationsLabel(item as ProductTableRow) }}
              </td>
              <td class="text-center">
                <span
                  v-if="typeof (item as ProductTableRow).default_price === 'object'"
                  class="ml-4"
                >
                  <span
                    v-money="{
                      amount: ((item as ProductTableRow).default_price as Price)?.unit_amount,
                    }"
                  />
                  <span class="text-grey pr-6 pl-1 text-caption">
                    {{ default_currency?.toUpperCase() }}
                  </span>
                </span>
                <span v-else> - </span>
              </td>
              <td v-if="!showGlobalLocationBasedUI" class="text-center">
                <div class="d-inline-flex" @click.stop>
                  <v-btn
                    prepend-icon="mdi-cart-outline"
                    data-cy="add-to-cart"
                    :disabled="isAddToCartDisabled(item as ProductTableRow)"
                    :color="isAddToCartDisabled(item as ProductTableRow) ? 'grey' : 'primary'"
                    @click.stop="sendToCart(item as ProductTableRow)"
                  >
                    {{ $t("common.cart.addToCart") }}
                  </v-btn>
                </div>
              </td>
              <td class="text-center">
                <v-menu v-model="(item as ProductTableRow).menuOpen">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon
                      flat
                      v-if="hasRoleAccessToAction([UserRoleStatus.ADMIN, UserRoleStatus.OWNER])"
                    >
                      <v-icon
                        color="grey"
                        @click.stop="
                          () => {
                            toggleMenu(item as ProductTableRow);
                          }
                        "
                      >
                        mdi-dots-vertical
                      </v-icon>
                    </v-btn>
                  </template>

                  <v-list>
                    <v-list-item
                      v-for="(action, index) in getActionList(item as ProductTableRow)"
                      :key="index"
                      :value="index"
                      @click.stop="
                        () => {
                          action.action((item as ProductTableRow).id);
                        }
                      "
                    >
                      <v-list-item-title>
                        <v-icon class="mr-2" :class="action.class">{{ action.icon }}</v-icon>
                        <span :class="action.class">
                          {{ action.title }}
                        </span>
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
        </v-data-table>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            @click="prevPage"
            :disabled="!hasPrev || productsListLoading"
            :loading="productsListLoading"
          >
            Prev
          </v-btn>
          <v-btn
            color="primary"
            @click="nextPage"
            :disabled="!hasNext || productsListLoading"
            :loading="productsListLoading"
          >
            Next
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-col>
  </v-row>

  <adjust-inventory-dialog ref="adjustInventoryDialog" @updated="searchProducts" />
</template>

<script lang="ts" setup>
import { useProductsStore } from "@/catalog/stores/products";
import { ref, computed, watch, onMounted, onUnmounted, watchEffect } from "vue";
import { storeToRefs } from "pinia";
import i18n from "@/app/plugins/i18n";
import { useRouter, useRoute } from "vue-router";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/app/plugins/firebase";
import { useAccountStore } from "@/account/stores/account";
import { useLocationStore } from "@/terminals/stores/locations";
import { UserRoleStatus } from "@/shared/enums/status.enum";
import { hasRoleAccessToAction } from "@/shared/utils/role.utils";
import StatusChip from "@/shared/components/Common/StatusChip.vue";
import { useCartStore } from "@/cart/stores/cart";
import { hasFeatureAccess } from "@/shared/utils/feature.utils";
import { PlatformFeature } from "@/shared/enums/feature.enum";
import ProductStockChips from "@/catalog/components/product/ProductStockChips.vue";
import ProductTagChip from "@/catalog/components/product-tag/ProductTagChip.vue";
import AdjustInventoryDialog from "@/catalog/components/inventory/AdjustInventoryDialog.vue";
import { getCartQuantity } from "@/cart/utils/cart.utils";
import default_product_image from "@/shared/assets/default_product_image.png";
import { normalizeLocationIds } from "@/terminals/utils/location.utils";
import type { Product, ProductClassRef } from "@/catalog/types/products";
import { toCartProductSnapshot } from "@/catalog/types/products";
import type { Price } from "@/catalog/types/prices";
import type { ProductTagItem } from "@/catalog/types/productTags";

// Define a proper interface for our product items
interface ProductAction {
  title: string;
  icon: string;
  action: (id: string) => void;
  class?: string;
}

// Row type: a store Product plus client-side fields read by this table
type ProductTableRow = Product & {
  checkoutCount?: number;
};

type SortItem = { key: string; order?: "asc" | "desc" | boolean };

const adjustInventoryDialog = ref<null | {
  open: (productIdOrObject: string | Product) => void;
}>(null);
const localMenuOpen = ref<string | null>(null);
const products = computed(() => useProductsStore().products);
const cartStore = useCartStore();
const hasNext = computed(() => useProductsStore().hasNext);
const hasPrev = computed(() => useProductsStore().hasPrev);
const accountStore = useAccountStore();
const accountId = computed(() => accountStore.account?.id ?? null);
const default_currency = computed(() => accountStore.account?.default_currency);
const router = useRouter();
const route = useRoute();
const productsStore = useProductsStore();
const productsListLoading = computed(() => productsStore.listLoading);
const locationStore = useLocationStore();
const { selectedLocation } = storeToRefs(locationStore);

const appBarLocationId = computed(() => selectedLocation.value?.id ?? null);

const locationBasedInventoryEnabled = computed(() => {
  return (
    accountStore.account?.accountSettings?.inventory?.location_based_inventory_enabled === true
  );
});

const isGlobalLocationSelected = computed(() => {
  const condition = productsStore.filterProductCondition;
  return "locationId" in condition && condition.locationId === null;
});

const cartLocationId = computed(() => {
  const condition = productsStore.filterProductCondition;
  if (!("locationId" in condition)) {
    return appBarLocationId.value;
  }
  const value = condition.locationId;
  return typeof value === "string" && value ? value : null;
});

const stockChipsLocationId = computed(() => {
  if (!locationBasedInventoryEnabled.value) return undefined;
  const value = cartLocationId.value;
  return typeof value === "string" && value ? value : undefined;
});

const cartQuantityLocationId = computed(() => {
  if (!locationBasedInventoryEnabled.value) return null;
  return cartLocationId.value;
});

const locationStockCountByProductId = ref<Record<string, number | undefined>>({});

watchEffect((onCleanup) => {
  const effectiveAccountId = accountId.value;
  const effectiveLocationId = cartLocationId.value;

  if (
    !locationBasedInventoryEnabled.value ||
    !effectiveAccountId ||
    typeof effectiveLocationId !== "string" ||
    !effectiveLocationId
  ) {
    locationStockCountByProductId.value = {};
    return;
  }

  locationStockCountByProductId.value = {};
  const unsubscribers: Array<() => void> = [];

  for (const product of products.value) {
    if (!product?.id) continue;
    if (product.hasInventory === false) continue;

    const productDocRef = doc(
      db,
      "accounts",
      effectiveAccountId,
      "locations",
      effectiveLocationId,
      "products",
      product.id,
    );

    const unsubscribe = onSnapshot(productDocRef, (docSnapshot) => {
      if (!docSnapshot.exists()) {
        return;
      }

      const data = docSnapshot.data() as { stockCount?: number };
      if (data?.stockCount === undefined) {
        return;
      }

      locationStockCountByProductId.value[product.id] = Number(data.stockCount ?? 0);
    });

    unsubscribers.push(unsubscribe);
  }

  onCleanup(() => {
    unsubscribers.forEach((unsubscribe) => unsubscribe());
  });
});

const showInventoryChips = computed(() => {
  if (!locationBasedInventoryEnabled.value) return true;
  return !isGlobalLocationSelected.value;
});

const showGlobalLocationBasedUI = computed(() => {
  return (
    hasFeatureAccess(PlatformFeature.INVENTORY) &&
    locationBasedInventoryEnabled.value &&
    isGlobalLocationSelected.value
  );
});

// Check if we're on the product class details page
const isProductClassDetailsPage = computed(() => {
  return route.name === "productClassDetails" || route.path.includes("/product-class/");
});

// Check if we're on the product tag details page
const isProductTagDetailsPage = computed(() => {
  return route.name === "productTagDetails" || route.path.includes("/product-tag/");
});

// The watch was causing duplicate searches - let the filter components trigger the search directly
// onMounted is still useful to ensure initial data loading
onMounted(() => {
  // Ensure we load products when the component is mounted
  if (!products.value.length) {
    searchProducts();
  }
});

const headers = computed(() => {
  const baseHeaders: {
    title: string;
    value: string;
    align: "start" | "center" | "end";
    sortable: boolean;
  }[] = [
    {
      title: "",
      value: "image",
      align: "start",
      sortable: false,
    },
    {
      title: i18n.global.t("common.action.name"),
      value: "name",
      align: "start",
      sortable: true,
    },
  ];

  // Only show product class column if not on the product class details page
  if (!isProductClassDetailsPage.value) {
    baseHeaders.push({
      title: i18n.global.t("product.productClass"),
      value: "product_class",
      align: "start",
      sortable: false,
    });
  }

  // Add status header
  baseHeaders.push({
    title: i18n.global.t("common.action.status"),
    value: "active",
    align: "center",
    sortable: true,
  });

  // Only show tags column if not on the product tag details page
  if (!isProductTagDetailsPage.value) {
    baseHeaders.push({
      title: i18n.global.t("common.action.tags"),
      value: "tags",
      align: "center",
      sortable: false,
    });
  }

  if (hasFeatureAccess(PlatformFeature.INVENTORY)) {
    baseHeaders.push({
      title: i18n.global.t("inventory.stock"),
      value: "stockCount",
      align: "center",
      sortable: false,
    });
  }

  if (showGlobalLocationBasedUI.value) {
    baseHeaders.push({
      title: i18n.global.t("page.locations.locations"),
      value: "locations",
      align: "center",
      sortable: false,
    });
  }

  // Add remaining headers
  baseHeaders.push({
    title: i18n.global.t("common.action.defaultPrice"),
    value: "default_price",
    align: "center",
    sortable: false,
  });

  if (!showGlobalLocationBasedUI.value) {
    baseHeaders.push({
      title: "",
      value: "action",
      align: "center",
      sortable: false,
    });
  }

  baseHeaders.push({
    title: "",
    value: "action",
    align: "center",
    sortable: false,
  });

  return baseHeaders;
});

const sortBy = ref<SortItem[]>([]);

const getSortPagination = (): { sortBy?: string; sortDirection?: string } => {
  const activeSort = sortBy.value?.[0];
  if (!activeSort?.key) {
    return {};
  }
  return {
    sortBy: activeSort.key,
    sortDirection: String(activeSort.order || "asc"),
  };
};

const nextPage = () => {
  const pagination = {
    limit: 10,
    nextPage: products.value[products.value.length - 1]?.id,
    ...getSortPagination(),
  };
  productsStore.searchProducts({ pagination }, false);
};

const prevPage = () => {
  const pagination = {
    limit: 10,
    prevPage: products.value[0]?.id,
    ...getSortPagination(),
  };
  productsStore.searchProducts({ pagination }, false);
};

const searchProducts = () => {
  productsStore.searchProducts({ pagination: getSortPagination() }, false);
};

const onSortByUpdate = () => {
  const activeSort = sortBy.value?.[0];
  const condition = { ...productsStore.filterProductCondition };
  if (activeSort?.key) {
    condition.sortBy = activeSort.key;
    condition.sortDirection = String(activeSort.order || "asc");
  } else {
    delete condition.sortBy;
    delete condition.sortDirection;
  }
  productsStore.setFilterProductCondition(condition);
  searchProducts();
};

const getActionList = (product: ProductTableRow): ProductAction[] => {
  const baseActions: ProductAction[] = [
    {
      title: i18n.global.t("common.action.editProduct"),
      icon: "mdi-pencil",
      action: (id: string) => {
        editProduct(id);
      },
    },
  ];

  // Add "Remove Tag" option when on product tag details page
  if (isProductTagDetailsPage.value && route.params.id) {
    baseActions.push({
      title: i18n.global.t("productTag.removeTag"),
      icon: "mdi-tag-remove",
      action: (id: string) => {
        removeTagFromProduct(id, route.params.id as string);
      },
    });
  }

  // Only add inventory-related actions if:
  // 1. The INVENTORY feature is accessible AND
  // 2. This row's product has inventory tracking enabled
  if (hasFeatureAccess(PlatformFeature.INVENTORY) && hasProductInventory(product)) {
    baseActions.push(
      {
        title: i18n.global.t("inventory.adjustInventory"),
        icon: "mdi-table-edit",
        action: (id: string) => {
          adjustInventory(id);
        },
      },
      {
        title: i18n.global.t("inventory.viewInventoryLog"),
        icon: "mdi-history",
        action: (id: string) => {
          viewInventoryLog(id);
        },
      },
    );
  }

  baseActions.push({
    title: product.active
      ? i18n.global.t("common.action.deactivate")
      : i18n.global.t("common.action.activate"),
    icon: product.active ? "mdi-cancel" : "mdi-check-circle",
    action: () => {
      activeOrInactive(product);
    },
    class: product.active ? "text-error" : "text-success",
  });

  return baseActions;
};

const resolveAddToCartStockCount = (item: ProductTableRow): number => {
  const effectiveLocationId = cartLocationId.value;
  const hasSpecificLocation =
    locationBasedInventoryEnabled.value &&
    typeof effectiveLocationId === "string" &&
    effectiveLocationId;

  if (hasSpecificLocation) {
    const snapshotStockCount = locationStockCountByProductId.value[item.id];
    if (typeof snapshotStockCount === "number") {
      return snapshotStockCount;
    }

    const cartItemStockCount = cartStore.items.find(
      (entry) => entry.productId === item.id && entry.locationId === effectiveLocationId,
    )?.stockCount;
    if (typeof cartItemStockCount === "number") {
      return cartItemStockCount;
    }
  } else {
    const globalSnapshotStockCount = cartStore.products.get(item.id)?.stockCount;
    if (typeof globalSnapshotStockCount === "number") {
      return globalSnapshotStockCount;
    }
  }

  return Number(item?.stockCount ?? 0);
};

const getInventoryLowerBound = (item: ProductTableRow): number | null => {
  if (item.canOversell !== true) return 0;
  // Only an explicit null means "no limit" (unlimited oversell). A missing
  // bound (older/imported docs) defaults to 0, matching the backend's
  // getEffectiveInventoryLowerBound, so the UI doesn't enable Add to cart for
  // items checkout would reject.
  if (item.inventoryLowerBound === null) return null;
  if (item.inventoryLowerBound === undefined) return 0;
  const lowerBound = Number(item.inventoryLowerBound);
  return Number.isFinite(lowerBound) ? lowerBound : 0;
};

const canAddInventoryItemToCart = (item: ProductTableRow): boolean => {
  const lowerBound = getInventoryLowerBound(item);
  if (lowerBound === null) return true;

  const cartQuantity = getCartQuantity(item, cartQuantityLocationId.value);
  const stockCount = resolveAddToCartStockCount(item);
  return stockCount - cartQuantity > lowerBound;
};

const getDefaultPriceId = (item: ProductTableRow): string | undefined =>
  typeof item.default_price === "object" && item.default_price !== null
    ? item.default_price.id
    : undefined;

const sendToCart = (item: ProductTableRow) => {
  const defaultPriceId = getDefaultPriceId(item);
  if (!item?.id || !defaultPriceId) return;

  const tracksInventory = item.hasInventory !== false;
  if (tracksInventory && !canAddInventoryItemToCart(item)) return;

  const cartProduct = toCartProductSnapshot(item);

  if (typeof cartStore.addProductWithPrice === "function") {
    cartStore.addProductWithPrice(cartProduct, defaultPriceId);
  } else {
    cartStore.addItem(cartProduct);
  }
};

const editProduct = (id: string) => {
  router.push({ path: `/update/product/${id}` });
};

const viewProductDetails = (item: string) => {
  router.push({ path: `/product/${item}` });
};

const viewInventoryLog = (id: string) => {
  router.push({
    path: "/inventory-log",
    query: { productId: id },
  });
};

const activeOrInactive = async (product: ProductTableRow) => {
  productsStore.listLoading = true;

  try {
    await productsStore.updateProduct({ active: !product.active }, product.id, {
      redirect: false,
      uploadImages: false,
    });
  } finally {
    productsStore.listLoading = false;
  }
};

const toggleMenu = (item: ProductTableRow) => {
  productsStore.selectedProduct = item;
  if (localMenuOpen.value && localMenuOpen.value === item.id) {
    localMenuOpen.value = null;
  } else {
    localMenuOpen.value = item.id;
  }
  item.menuOpen = !item.menuOpen;
};

// Keep this for now, but currently not needed - was causing issues with cart drawer
const cleanupProductListeners = () => {
  products.value.forEach((product: ProductTableRow) => {
    if (product.id) {
      cartStore.removeProductListener(product.id);
    }
    if (typeof product.product_class === "object" && product.product_class?.id) {
      cartStore.removeProductClassListener(product.product_class.id);
    }
  });
};

const isAddToCartDisabled = (item: ProductTableRow) => {
  if (!getDefaultPriceId(item)) return true;

  const tracksInventory = item.hasInventory !== false;
  if (!tracksInventory) return false;

  return item.active === false || !canAddInventoryItemToCart(item);
};

const getProductClass = (item: ProductTableRow): ProductClassRef | null => {
  // Check if product_class exists directly on the item
  if (item.product_class && typeof item.product_class === "object") {
    return item.product_class;
  }

  // Check if product_class is in metadata
  const metadataProductClass = item.metadata?.product_class;
  if (metadataProductClass) {
    try {
      // Try to parse it if it's a JSON string
      const parsed: unknown = JSON.parse(metadataProductClass);
      if (parsed && typeof parsed === "object") {
        return parsed as ProductClassRef;
      }
      return null;
    } catch {
      return null;
    }
  }

  return null;
};

const adjustInventory = (id: string) => {
  // Find the product object in our products array
  const productObject = products.value.find((product: ProductTableRow) => product.id === id);
  if (productObject) {
    adjustInventoryDialog.value?.open(productObject);
  } else {
    // If product not found in the array, use the ID directly, and the dialog
    // will fetch the product details
    adjustInventoryDialog.value?.open(id);
  }
};

// Helper function to safely check if a product has tags
const getProductTags = (item: ProductTableRow): ProductTagItem[] => {
  if (item && Array.isArray(item.tags)) {
    return item.tags;
  }
  return [];
};

// Helper function to check if a product has tags
const hasProductTags = (item: ProductTableRow) => {
  const tags = getProductTags(item);
  return tags.length > 0;
};

// Helper function to check if a product has inventory
const hasProductInventory = (item: ProductTableRow | null | undefined): boolean => {
  if (!item) return false;
  return item.hasInventory !== false; // Consider undefined/null as having inventory (default behavior)
};

const getProductLocationsLabel = (item: ProductTableRow | null | undefined) => {
  if (!item) return "-";
  const raw = item.assignedLocationIds;
  const hasAssignedLocationIds = Object.prototype.hasOwnProperty.call(item, "assignedLocationIds");

  if (raw === null) return i18n.global.t("common.action.all");
  if (!hasAssignedLocationIds || raw === undefined) return i18n.global.t("common.action.none");

  const ids = normalizeLocationIds(raw);
  if (!Array.isArray(ids)) return i18n.global.t("common.action.none");
  return String(ids.length);
};

// Helper function to remove the specified tag from a product
const removeTagFromProduct = async (productId: string, tagId: string) => {
  // Find the product in our list
  const product = products.value.find((p: ProductTableRow) => p.id === productId);
  if (!product) return;

  productsStore.listLoading = true;

  try {
    // Get the current tags and filter out the tag we want to remove
    const updatedTags = getProductTags(product)
      .filter((tag: ProductTagItem) => tag.id !== tagId)
      .map((tag: ProductTagItem) => tag.id); // We just need the IDs for the update

    // Update the product with the filtered tags array
    await productsStore.updateProduct({ tags: updatedTags }, productId);

    // Refresh the products list to show the changes
    await searchProducts();
  } catch (error) {
    console.error("Error removing tag from product:", error);
  } finally {
    productsStore.listLoading = false;
  }
};
</script>
<style>
#productTable .v-data-table-footer {
  display: none !important;
}
</style>
